import { ChangeEvent, KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { NumericFormat } from "react-number-format";
import IProcessed_Report from "@models/IProcessed_Report";
import IPriceGapReportRow from "@models/IPriceGapReportRow";
import { IPriceGapReportSummary } from "@models/IPriceGapReportOptions";
import IOwTableCols from "@models/IOwTableCols";
import QueueStatusEnum from "@models/QueueStatusEnum";
import SortOrderEnum from "@models/SortOrderEnum";
import EndpointsEnum from "../../../../models/EndpointsEnum";
import IGetReportProductsDTO from "../../../../models/DTO/IGetReportProductsDTO";
import IOnGetReportProductsDTO from "../../../../models/DTO/IOnGetReportProductsDTO";
import IDownloadReportExcelDTO from "../../../../models/DTO/IDownloadReportExcelDTO";
import IOnDownloadReportExcelDTO from "../../../../models/DTO/IOnDownloadReportExcelDTO";
import UserService from "../../../../services/UserService";
import UIService from "../../../../services/UIService";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import OwTitle from "../../../shared/OwTitle/OwTitle";
import OwButton from "../../../shared/OwButton/OwButton";
import { OwFnInput } from "../../../shared/OwFnInput/OwFnInput";
import { OwFnTable } from "../../../shared/OwFnTable/OwFnTable";
import PaginationBar from "../../../shared/PaginationBar/PaginationBar";
import ReportPeriod from "../ReportPeriod/ReportPeriod";
import { useAppContextNonNull } from "@contexts/AppContext";
import { flattenPriceGapRows, getStatusClass } from "./PriceGapReport.helpers";
import { PriceGapStatusHelp } from "./PriceGapStatusHelp";
import "./PriceGapReport.scss";

type Props = {
    processedReport: IProcessed_Report;
};

type SortBy = NonNullable<IGetReportProductsDTO["sortBy"]>;
type SortDirection = NonNullable<IGetReportProductsDTO["sortOrder"]>;

/** Shape of `Processed_Report.report_data` written by the API for this report type (rows live in detail rows). */
interface IPriceGapReportData {
    filename?: string;
    summary?: IPriceGapReportSummary;
    tolerance?: { percent: number; amount: number };
    dateRange?: { start: string; end: string };
}

export const PriceGapReport = (props: Props) => {
    const { userModel, supplierModel, socket } = useAppContextNonNull();
    const intl = useIntl();

    const report = props.processedReport;
    // Queue-only report: rows and summary only exist once the cron worker has completed it.
    const isReady = report.status === QueueStatusEnum.COMPLETED;
    const reportData = (report.report_data || {}) as IPriceGapReportData;
    const summary = reportData.summary;
    const pricingPrecision = supplierModel.pricing_precision ?? 2;

    const [rows, setRows] = useState<IPriceGapReportRow[]>([]);
    const [offset, setOffset] = useState(0); // PaginationBar uses a 0-based page index
    const [limit, setLimit] = useState(25);
    const [totalRows, setTotalRows] = useState(report.total_items || 0);
    const [loading, setLoading] = useState(false);
    const [downloadingCsv, setDownloadingCsv] = useState(false);
    const [skuInput, setSkuInput] = useState("");
    const [searchSku, setSearchSku] = useState("");
    const [sortBy, setSortBy] = useState<SortBy>("sort_order");
    const [sortOrder, setSortOrder] = useState<SortDirection>("ASC");
    // The API echoes page/limit (but no request ID) on the response, so this is what identifies the latest request.
    const latestRequestRef = useRef<{ page: number; limit: number } | null>(null);

    const fetchRows = () => {
        setLoading(true);

        const payload: IGetReportProductsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            reportGuid: report.guid,
            page: offset + 1,
            limit,
            searchSku: searchSku || undefined,
            sortBy,
            sortOrder,
        };

        latestRequestRef.current = { page: offset + 1, limit };
        socket.emit(EndpointsEnum.ReportGetReportProducts, payload);
    };

    const onGetReportProducts = (data: IOnGetReportProductsDTO) => {
        // Drop out-of-order responses so a slow earlier page can't overwrite the one the user is now on.
        const latest = latestRequestRef.current;
        if (latest && (data.page !== latest.page || data.limit !== latest.limit)) {
            return;
        }

        setRows(flattenPriceGapRows(data.catalogItems));
        setTotalRows(data.total);
        setLoading(false);
    };

    const handleDownloadCsv = () => {
        setDownloadingCsv(true);

        const payload: IDownloadReportExcelDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            reportGuid: report.guid,
        };

        socket.emit(EndpointsEnum.ReportDownloadExcel, payload);
    };

    const onDownloadCsv = (data: IOnDownloadReportExcelDTO) => {
        setDownloadingCsv(false);

        if (data.downloadDirect && data.buffer) {
            downloadFromBase64(data.buffer, data.filename);
        } else if (data.s3Url) {
            window.location.assign(data.s3Url);
        }
    };

    // The API stores this report's file as a base64 CSV (it round-trips through the price group CSV import).
    const downloadFromBase64 = (base64: string, filename: string) => {
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: "text/csv" });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const applySkuSearch = () => {
        setSearchSku(skuInput.trim());
        setOffset(0);
    };

    useEffect(() => {
        if (!isReady) return;

        socket.on(EndpointsEnum.ReportOnGetReportProducts, onGetReportProducts);
        socket.on(EndpointsEnum.ReportOnDownloadExcel, onDownloadCsv);

        return () => {
            socket.removeListener(EndpointsEnum.ReportOnGetReportProducts, onGetReportProducts);
            socket.removeListener(EndpointsEnum.ReportOnDownloadExcel, onDownloadCsv);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isReady]);

    useEffect(() => {
        if (isReady) {
            fetchRows();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isReady, offset, limit, searchSku, sortBy, sortOrder]);

    const money = (value: number | null | undefined) => {
        if (value === null || value === undefined) {
            return "";
        }

        return (
            <NumericFormat
                value={value}
                displayType={"text"}
                thousandSeparator={true}
                prefix={"$"}
                decimalScale={pricingPrecision}
                fixedDecimalScale={true}
            />
        );
    };

    const columns: IOwTableCols[] = useMemo(() => {
        // Only these four columns exist on Processed_Report_Detail, so only they can sort server-side.
        // OwSortHeader always translates the title, so sortable columns use message ids.
        const sortable = (title: string, valueProp: keyof IPriceGapReportRow, sortCol: SortBy): IOwTableCols => ({
            title,
            valueProp,
            sortCol,
            isSortable: true,
            sortOrder: sortOrder.toLowerCase() as SortOrderEnum,
            sortFn: (_title: string, order: SortOrderEnum) => {
                setSortBy(sortCol);
                setSortOrder(order === SortOrderEnum.ASC ? "ASC" : "DESC");
                setOffset(0);
            },
        });

        // Non-sortable columns mirror the CSV headers so the screen matches the downloaded file.
        const plain = (title: string, valueProp: keyof IPriceGapReportRow): IOwTableCols => ({
            title,
            valueProp,
            skipTitleTranslate: true,
        });

        const moneyCol = (title: string, valueProp: keyof IPriceGapReportRow): IOwTableCols => ({
            ...plain(title, valueProp),
            className: "text-end",
            renderFnEmitsRow: true,
            renderFn: (row: IPriceGapReportRow) => money(row[valueProp] as number | null),
        });

        const dateCol = (title: string, valueProp: keyof IPriceGapReportRow): IOwTableCols => ({
            ...plain(title, valueProp),
            renderFnEmitsRow: true,
            renderFn: (row: IPriceGapReportRow) => (row[valueProp] ? UserService.parseUTCDate(row[valueProp]) : ""),
        });

        return [
            sortable("rank", "rank", "sort_order"),
            plain("Customer", "customer"),
            plain("Price Group", "priceGroup"),
            sortable("sku", "sku", "sku"),
            plain("Item Name", "itemName"),
            { ...sortable("times_ordered", "timesOrdered", "total_qty"), className: "text-end" },
            dateCol("First Ordered", "firstOrdered"),
            dateCol("Last Ordered", "lastOrdered"),
            moneyCol("Last Invoiced Price", "lastInvoicedPrice"),
            moneyCol("Min Invoiced", "minInvoicedPrice"),
            moneyCol("Max Invoiced", "maxInvoicedPrice"),
            { ...plain("Distinct Prices", "distinctPrices"), className: "text-end" },
            moneyCol("Price Group Price", "priceGroupPrice"),
            moneyCol("MSRP", "msrp"),
            moneyCol("Effective Price Today", "effectivePrice"),
            moneyCol("Variance $", "varianceAmount"),
            {
                ...plain("Variance %", "variancePercent"),
                className: "text-end",
                renderFnEmitsRow: true,
                renderFn: (row: IPriceGapReportRow) =>
                    row.variancePercent === null || row.variancePercent === undefined ? "" : `${row.variancePercent.toFixed(2)}%`,
            },
            {
                ...sortable("exposure", "exposure", "total_net"),
                className: "text-end",
                renderFnEmitsRow: true,
                renderFn: (row: IPriceGapReportRow) => money(row.exposure),
            },
            {
                ...plain("Status", "status"),
                renderFnEmitsRow: true,
                renderFn: (row: IPriceGapReportRow) => <span className={getStatusClass(row.status)}>{row.status}</span>,
            },
            moneyCol("Suggested Price", "suggestedPrice"),
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortOrder, pricingPrecision]);

    const statusCounts = Object.entries(summary?.byStatus || {}).filter(([, count]) => count > 0);

    return (
        <div className={"PriceGapReport"}>
            <div className={"row"}>
                <OwTitle className={"col-12"} title={"price_gap_report"} tag={"h3"} />
                <ReportPeriod className={"col-12 mb-3"} startDate={report.period_start} endDate={report.period_end} />
            </div>

            {!isReady ? (
                <div className={"text-muted"}>
                    <OwLabel title={"report_not_ready"} />
                </div>
            ) : (
                <div>
                    {summary ? (
                        <div className={"card mb-3 p-3 " + (UIService.isMainViewLight() ? "" : "bg-dark text-light")}>
                            <div className={"d-flex flex-wrap gap-4 mb-2"}>
                                <span>
                                    <strong>{intl.formatMessage({ id: "customers_affected" })}:</strong> {summary.customersAffected}
                                </span>
                                <span>
                                    <strong>{intl.formatMessage({ id: "total_exposure" })}:</strong> {money(summary.totalAbsExposure)}
                                </span>
                                {reportData.tolerance ? (
                                    <span>
                                        <strong>{intl.formatMessage({ id: "tolerance" })}:</strong> {reportData.tolerance.percent}% /{" "}
                                        {money(reportData.tolerance.amount)}
                                    </span>
                                ) : null}
                                {summary.hiddenOkRows > 0 ? (
                                    <span>
                                        <strong>{intl.formatMessage({ id: "hidden_ok_rows" })}:</strong> {summary.hiddenOkRows}
                                    </span>
                                ) : null}
                            </div>
                            {statusCounts.length > 0 ? (
                                <div className={"d-flex flex-wrap gap-3"}>
                                    <strong>{intl.formatMessage({ id: "rows_by_status" })}:</strong>
                                    {statusCounts.map(([status, count]) => (
                                        <span key={status} className={getStatusClass(status)}>
                                            {status}: {count}
                                        </span>
                                    ))}
                                </div>
                            ) : null}
                            <PriceGapStatusHelp className={"mt-3"} />
                        </div>
                    ) : (
                        <PriceGapStatusHelp className={"mb-3"} />
                    )}

                    <div className={"d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3"}>
                        <div className={"d-flex align-items-center gap-2"}>
                            <OwLabel
                                className={"me-3 no-wrap"}
                                title={downloadingCsv ? "generating_csv" : "download_csv"}
                                leftIconClass={downloadingCsv ? "fas fa-spinner fa-spin" : "fas fa-download"}
                                fn={handleDownloadCsv}
                            />
                            <OwFnInput
                                name={"searchSku"}
                                value={skuInput}
                                placeholder={"sku"}
                                fn={(e: ChangeEvent<HTMLInputElement>) => setSkuInput(e.target.value)}
                                onKeyDownFn={(e: KeyboardEvent<HTMLInputElement>) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        applySkuSearch();
                                    }
                                }}
                                noOuterDiv={true}
                                isSmall={true}
                            />
                            <OwButton title={"search"} fn={applySkuSearch} className={"btn-primary btn-sm"} />
                        </div>
                        <span>
                            <strong>{intl.formatMessage({ id: "total_rows" })}:</strong> {totalRows}
                        </span>
                    </div>

                    {loading ? (
                        <div className={"text-center p-4"}>
                            <i className={"fas fa-spinner fa-spin fa-2x"} />
                        </div>
                    ) : (
                        <div className={"price-gap-table"}>
                            <OwFnTable
                                columns={columns}
                                dataSet={rows}
                                dataFetch={false}
                                initialData={false}
                                currentSortCol={sortBy}
                                tableConfig={{ tableStriped: true, tableHover: true, tableSmall: true }}
                            />
                        </div>
                    )}

                    {totalRows > 0 ? (
                        <div className={"row mt-3"}>
                            <div className={"col-12"}>
                                <PaginationBar
                                    offset={offset}
                                    limit={limit}
                                    totalRecords={totalRows}
                                    onPaginationChanged={(newOffset, newLimit) => {
                                        setOffset(newOffset);
                                        setLimit(newLimit);
                                    }}
                                    isActive={loading}
                                    hideLimit={false}
                                    centered={true}
                                />
                            </div>
                        </div>
                    ) : null}
                </div>
            )}
        </div>
    );
};
